import React from "react";
import styled from "styled-components";

import Modal from "../../../components/modal/Modal";
import type {
  NoteChangeConflictData,
  NoteChangePayload,
} from "../../../app/services/notes";
import ChangeDiff from "./ChangeDiff";
import NoteEditor from "./NoteEditor";
import { toChangePayload } from "./ChangeEditModal";

/**
 * マージ時のコンフリクト解消モーダル。
 *
 * PR 作成後に対象が直接更新されて base とずれた場合（409）に開く。
 * 「現在の内容」と「この提案」の差分を見比べながら、下のエディタで
 * 最終内容を調整して「この内容でマージ」する（Git のコンフリクト解消と
 * 同じく、確定内容をそのままコミットする）。
 */

const DiffColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DiffColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export interface ConflictResolveModalProps {
  targetType: "note" | "card";
  conflict: NoteChangeConflictData["data"];
  merging: boolean;
  onResolve: (payload: NoteChangePayload) => void;
  onClose: () => void;
}

const ConflictResolveModal: React.FC<ConflictResolveModalProps> = ({
  targetType,
  conflict,
  merging,
  onResolve,
  onClose,
}) => {
  const { base, current, proposed } = conflict;

  return (
    <Modal
      title="コンフリクトの解消"
      titleSize="medium"
      description="この提案の作成後に対象が更新されています。両方の変更を確認し、最終内容を調整してマージしてください。"
      size="wide"
      onClose={onClose}
    >
      <div className="u-col u-gap16" style={{ alignItems: "stretch" }}>
        <DiffColumns>
          <DiffColumn>
            <h3 className="heading-13 u-line-height-150">
              現在の内容（マージ先で更新済み）
            </h3>
            <ChangeDiff
              before={base}
              after={current ?? proposed}
              isCard={targetType === "card"}
            />
          </DiffColumn>
          <DiffColumn>
            <h3 className="heading-13 u-line-height-150">この提案の変更</h3>
            <ChangeDiff
              before={base}
              after={proposed}
              isCard={targetType === "card"}
            />
          </DiffColumn>
        </DiffColumns>

        <div className="u-col u-gap8" style={{ alignItems: "stretch" }}>
          <h3 className="heading-13 u-line-height-150">
            最終内容（この内容でマージされます）
          </h3>
          <NoteEditor
            initialTitle={proposed.title}
            initialContent={proposed.content}
            initialMeta={{ ...proposed, display_style: targetType }}
            saving={merging}
            saveLabel="この内容でマージ"
            onSave={(raw) => onResolve(toChangePayload(targetType, raw))}
            onCancel={onClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ConflictResolveModal;
